// ========= START: bp-tango-dev/src/components/TangoSetupScreen.js (UPDATED CATEGORIES) =========
import React, { useState, useMemo, useEffect, useRef } from 'react';
import vocabulary from '../data/pronunciation_vocab.js';

export const CATEGORIES = [
  // --- People & Feelings ---
  { id: 'feelings', name: 'Feelings', image: '/assets/images/categories/category_feelings.png' },
  { id: 'family', name: 'Family', image: '/assets/images/categories/category_family.png' },
  { id: 'body', name: 'Body', image: '/assets/images/categories/category_body.png' },
  { id: 'jobs', name: 'Jobs', image: '/assets/images/categories/category_jobs.png' },

  // --- Food & Drink ---
  { id: 'food', name: 'Food', image: '/assets/images/categories/category_food.png' },
  { id: 'fruit', name: 'Fruit', image: '/assets/images/categories/category_fruit.png' },
  { id: 'vegetables', name: 'Vegetables', image: '/assets/images/categories/category_vegetables.png' },

  // --- Animals & Nature ---
  { id: 'animals', name: 'Animals', image: '/assets/images/categories/category_animals.png' },
  { id: 'nature', name: 'Nature', image: '/assets/images/categories/category_nature.png' },
  { id: 'weather', name: 'Weather', image: '/assets/images/categories/category_weather.png' },

  // --- School Life ---
  { id: 'school_subjects', name: 'School Subjects', image: '/assets/images/categories/category_school_subjects.png' },
  { id: 'stationery', name: 'Stationery', image: '/assets/images/categories/category_stationery.png' },
  { id: 'school_places', name: 'School Places', image: '/assets/images/categories/category_school_places.png' },
  { id: 'school_events', name: 'School Events', image: '/assets/images/categories/category_school_events.png' },
  { id: 'club_activities', name: 'Club Activities', image: '/assets/images/categories/category_club_activities.png' },

  // --- Daily Life & Hobbies ---
  { id: 'daily_activities', name: 'Daily Activities', image: '/assets/images/categories/category_daily_activities.png' },
  { id: 'hobbies', name: 'Hobbies', image: '/assets/images/categories/category_hobbies.png' },
  { id: 'sports', name: 'Sports', image: '/assets/images/categories/category_sports.png' },
  { id: 'musical_instruments', name: 'Instruments', image: '/assets/images/categories/category_musical_instruments.png' },
  { id: 'items', name: 'Items', image: '/assets/images/categories/category_items.png' },

  // --- Places & World ---
  { id: 'places', name: 'Places', image: '/assets/images/categories/category_places.png' },
  { id: 'countries', name: 'Countries', image: '/assets/images/categories/category_countries.png' },
  { id: 'transportation', name: 'Transportation', image: '/assets/images/categories/category_transportation.png' },

  // --- Time ---
  { id: 'days', name: 'Days', image: '/assets/images/categories/category_days.png' },
  { id: 'months', name: 'Months', image: '/assets/images/categories/category_months.png' },
  { id: 'seasons', name: 'Seasons', image: '/assets/images/categories/category_seasons.png' },
  { id: 'time_expressions', name: 'Time Expressions', image: '/assets/images/categories/category_time_expressions.png' },

  // --- Abstract & Grammar ---
  { id: 'verbs', name: 'Verbs', image: '/assets/images/categories/category_verbs.png' },
  { id: 'adjectives', name: 'Adjectives', image: '/assets/images/categories/category_adjectives.png' },
  { id: 'colors', name: 'Colors', image: '/assets/images/categories/category_colors.png' },
  { id: 'numbers', name: 'Numbers', image: '/assets/images/categories/category_numbers.png' },
  { id: 'shapes', name: 'Shapes', image: '/assets/images/categories/category_shapes.png' },
  { id: 'concepts', name: 'Concepts', image: '/assets/images/categories/category_concepts.png' },
  { id: 'frequency_adverbs', name: 'How Often?', image: '/assets/images/categories/category_frequency_adverbs.png' },

  // --- Culture ---
  { id: 'japanese_events', name: 'Japanese Events', image: '/assets/images/categories/category_japanese_events.png' },
];

const GRADE_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const displayGrade = (grade) => {
  if (grade > 6) return `JHS ${grade - 6}`;
  return `G${grade}`;
};

function TangoSetupScreen({ onSelectionChange }) {
  const [selectedGrade, setSelectedGrade] = useState(3);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const categoryContainerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [gridStyles, setGridStyles] = useState({ gridTemplateColumns: '', buttonSize: 0 });

  useEffect(() => {
    onSelectionChange({
      grade: selectedGrade,
      categories: selectedCategories,
    });
  }, [selectedGrade, selectedCategories, onSelectionChange]);

  const availableCategories = useMemo(() => {
    // Filter vocabulary to see which categories actually have words for this grade
    const categoriesForGrade = new Set(
      vocabulary
        .filter(word => word.grade <= selectedGrade)
        .map(word => word.category)
    );
    return CATEGORIES.map(cat => ({
      ...cat,
      isAvailable: categoriesForGrade.has(cat.id),
    }));
  }, [selectedGrade]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setContainerSize({ width, height });
      }
    });
    const currentRef = categoryContainerRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, []);

  // Dynamic Layout Logic to maximize button size within the container
  useEffect(() => {
    if (containerSize.width === 0 || availableCategories.length === 0) return;

    const calculateBestLayout = () => {
      const categoryCount = availableCategories.length;
      const { width, height } = containerSize;
      const gap = 15; // Must match CSS gap
      let bestLayout = { cols: 0, buttonSize: 0, area: 0 };

      for (let cols = 1; cols <= categoryCount; cols++) {
        const rows = Math.ceil(categoryCount / cols);

        const totalGapWidth = (cols - 1) * gap;
        const totalGapHeight = (rows - 1) * gap;

        const widthPerSlot = (width - totalGapWidth) / cols;
        const heightPerSlot = (height - totalGapHeight) / rows;

        const buttonSize = Math.floor(Math.min(widthPerSlot, heightPerSlot));
        const area = buttonSize * buttonSize;

        if (area > bestLayout.area) {
          bestLayout = { cols, buttonSize, area };
        }
      }

      setGridStyles({
        gridTemplateColumns: `repeat(${bestLayout.cols}, ${bestLayout.buttonSize}px)`,
        buttonSize: bestLayout.buttonSize,
      });
    };
    calculateBestLayout();
  }, [containerSize, availableCategories]);


  // ========= START: src/components/TangoSetupScreen.js - HANDLE GRADE CHANGE =========
  const handleGradeChange = (newGrade) => {
    setSelectedGrade(newGrade);

    // 1. Find which categories are actually valid for the NEW grade level
    const categoriesForNewGrade = new Set(
      vocabulary
        .filter(word => word.grade <= newGrade)
        .map(word => word.category)
    );

    // 2. Filter the CURRENT selection. 
    // We keep a category only if it is available in the new grade.
    setSelectedCategories(prev => prev.filter(catId => categoriesForNewGrade.has(catId)));
  };
  // ========= END: src/components/TangoSetupScreen.js - HANDLE GRADE CHANGE =========

  const handleCategoryToggle = (categoryId, isAvailable) => {
    if (!isAvailable) return;
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <div className="tango-setup-screen">
      <div className="tango-setup-section">
        <h2 className="tango-setup-header">Choose Grade Level</h2>
        <div className="tango-grade-level-container">
          {GRADE_LEVELS.map(grade => (
            <button
              key={grade}
              className={`tango-grade-button ${selectedGrade === grade ? 'selected' : ''}`}
              onClick={() => handleGradeChange(grade)}
            >
              {displayGrade(grade)}
            </button>
          ))}
        </div>
      </div>

      <div className="tango-setup-section tango-category-picker">
        <h2 className="tango-setup-header">Choose Categories</h2>
        <div
          ref={categoryContainerRef}
          className="tango-category-container"
          style={{ gridTemplateColumns: gridStyles.gridTemplateColumns }}
        >
          {availableCategories.map(category => {
            const isSelected = selectedCategories.includes(category.id);
            let buttonClass = 'tango-category-button';
            if (isSelected) buttonClass += ' selected';
            if (!category.isAvailable) buttonClass += ' disabled';

            return (
              <button
                key={category.id}
                className={buttonClass}
                onClick={() => handleCategoryToggle(category.id, category.isAvailable)}
                disabled={!category.isAvailable}
                style={{ width: `${gridStyles.buttonSize}px`, height: `${gridStyles.buttonSize}px` }}
              >
                <img src={`${process.env.PUBLIC_URL}${category.image}`} alt={category.name} className="tango-category-image" />
                <span className="tango-category-text">{category.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default TangoSetupScreen;
// ========= END: bp-tango-dev/src/components/TangoSetupScreen.js (UPDATED CATEGORIES) =========